import {CommonModule, isPlatformBrowser} from '@angular/common';
import {ChangeDetectorRef, Component, Inject, Input, OnDestroy, PLATFORM_ID} from '@angular/core';
import {MediaObjectInterface} from '../../../core/interfaces/media-object.interface';
import {MediaObjectPipe} from '../../../core/pipes/media-object.pipe';
import {MediaTypeEnum} from '../../../core/enums/media-type.enum';
import {ImageLightboxComponent} from '../../components/image-lightbox/image-lightbox.component';
import {ImageLightboxItem, ImageLightboxThumbnail} from '../../components/image-lightbox/image-lightbox.model';

@Component({
  selector: 'app-comment-media',
  standalone: true,
  imports: [CommonModule, ImageLightboxComponent],
  template: `
    <div *ngIf="media?.length" class="comment-media">
      <app-image-lightbox *ngFor="let image of images; trackBy: trackImage" class="comment-media__item"
                         [images]="images" [imageId]="image.id" [thumbnail]="thumbnail"
                         (loaded)="onLoaded($event)" (loadFailed)="onLoadFailed($event)" (retry)="retryImage($event)">
      </app-image-lightbox>
    </div>
  `,
  styles: [`
    .comment-media { display: flex; flex-wrap: wrap; gap: .5rem; margin: .5rem 0; }
    .comment-media__item { width: 96px; height: 96px; position: relative; }
  `]
})
export class CommentMediaComponent implements OnDestroy {
  private _media: MediaObjectInterface[] = [];
  private readonly readyUrls = new Set<string>();
  private readonly failedUrls = new Set<string>();
  private readonly loadingUrls = new Set<string>();
  private readonly retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly preloaders = new Map<string, HTMLImageElement>();
  private destroyed = false;
  public images: ImageLightboxItem[] = [];
  public readonly thumbnail: ImageLightboxThumbnail = {
    width: '96px', height: '96px', fit: 'cover', borderRadius: '.5rem',
  };

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object, private readonly cdr: ChangeDetectorRef) {}

  @Input() set media(value: MediaObjectInterface[]) {
    this._media = (value || []).filter(image => image && (image.type
      ? image.type === MediaTypeEnum.IMAGE : !image.mimeType || image.mimeType.startsWith('image/')));
    this.syncImages();
    if (isPlatformBrowser(this.platformId)) {
      this._media.forEach(image => this.preload(image));
    }
  }

  get media(): MediaObjectInterface[] {
    return this._media;
  }

  imageUrl(image: MediaObjectInterface): string {
    return MediaObjectPipe.transformToLink(image, {formatType: 'thumb_md', defaultImageType: 'loading'});
  }

  private syncImages(): void {
    this.images = this.media.map((image, index) => {
      const url = this.imageUrl(image);
      const id = String(image['@id'] || image.id || `${url}:${index}`);
      const item = this.images.find(item => item.id === id) || {id} as ImageLightboxItem;
      return Object.assign(item, {
        thumbnailUrl: url, previewUrl: url, alt: image.originalName || image.name || '',
        state: !url || this.failedUrls.has(url) ? 'error' : this.readyUrls.has(url) ? 'ready' : 'loading',
      });
    });
  }

  trackImage(_: number, image: ImageLightboxItem): string { return image.id; }

  onLoaded(id: string): void {
    const image = this.images.find(image => image.id === id);
    if (image?.thumbnailUrl && !this.readyUrls.has(image.thumbnailUrl)) {
      this.readyUrls.add(image.thumbnailUrl);
      this.failedUrls.delete(image.thumbnailUrl);
      this.syncImages();
    }
  }

  onLoadFailed(id: string): void {
    const image = this.images.find(image => image.id === id);
    if (image?.thumbnailUrl && this.readyUrls.has(image.thumbnailUrl)) {
      this.readyUrls.delete(image.thumbnailUrl);
      this.failedUrls.add(image.thumbnailUrl);
      this.syncImages();
    }
  }

  retryImage(id: string): void {
    const index = this.images.findIndex(image => image.id === id);
    const image = this.media[index];
    if (!image) return;
    const url = this.imageUrl(image);
    this.failedUrls.delete(url);
    this.readyUrls.delete(url);
    this.syncImages();
    this.preload(image);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.retryTimers.forEach(timer => clearTimeout(timer));
    this.preloaders.forEach(preloader => {
      preloader.onload = null;
      preloader.onerror = null;
    });
    this.retryTimers.clear();
    this.preloaders.clear();
  }

  private preload(image: MediaObjectInterface, attempt = 0): void {
    const url = this.imageUrl(image);
    if (!isPlatformBrowser(this.platformId) || !url || this.readyUrls.has(url) || this.loadingUrls.has(url) || this.destroyed) {
      return;
    }

    this.loadingUrls.add(url);
    const preloader = new Image();
    this.preloaders.set(url, preloader);
    preloader.onload = () => {
      this.loadingUrls.delete(url);
      this.preloaders.delete(url);
      this.readyUrls.add(url);
      this.failedUrls.delete(url);
      this.syncImages();
      this.cdr.markForCheck();
    };
    preloader.onerror = () => {
      this.loadingUrls.delete(url);
      this.preloaders.delete(url);
      if (attempt < 7 && !this.destroyed) {
        const timer = setTimeout(() => {
          this.retryTimers.delete(url);
          this.preload(image, attempt + 1);
        }, 1500);
        this.retryTimers.set(url, timer);
      } else if (!this.destroyed) {
        this.failedUrls.add(url);
        this.syncImages();
        this.cdr.markForCheck();
      }
    };
    preloader.src = url;
  }
}
